import { axiosInstance } from '@/lib/axios'
import {
  AssignChartScanPayload,
  ChartScanActivity,
  ChartScanDetail,
  ChartScanList,
  ChartScanListFilter,
  ChartScanDiagnostic,
  ChartScanUploadResponse,
  UpdateDiscPayload,
  UpdateDiscResponse,
} from './types'

const base = (companyId: string | number) => `/api/${companyId}/chart-scan`

export function getChartScans(companyId: string | number, filter: ChartScanListFilter, page: number) {
  return axiosInstance.get<ChartScanList>(base(companyId), {
    params: { filter, page, pageSize: 25 },
  })
}

export function uploadChartScan(companyId: string | number, file: File) {
  const form = new FormData()
  form.append('file', file)
  return axiosInstance.post<ChartScanUploadResponse>(`${base(companyId)}/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export function getChartScan(companyId: string | number, scanId: number) {
  return axiosInstance.get<ChartScanDetail>(`${base(companyId)}/${scanId}`)
}

// Load a single chart (disc) by its id — used by the driver daily-activity view's
// "Pokaż tarczkę" action to open the chart editor for an analog-from-chart activity.
// Returns a single-disc ChartScanDetail, same shape the scan editor consumes.
// includeSiblings pulls back every disc of the parent scan (a scanned sheet often
// holds several) so the editor can page through them. Off by default: "Pokaż
// tarczkę" opens one analog activity's chart and wants exactly that one.
export function getChartDisc(
  companyId: string | number,
  discId: number,
  includeSiblings = false,
) {
  return axiosInstance.get<ChartScanDetail>(`${base(companyId)}/disc/${discId}`, {
    params: includeSiblings ? { includeSiblings: true } : undefined,
  })
}

// 1:1 with WPF AssignLoadedImageToDriverView "Save". Persists scan-level
// metadata and cascades it to the Ignored child discs.
export function assignChartScan(
  companyId: string | number,
  scanId: number,
  payload: AssignChartScanPayload,
) {
  return axiosInstance.post<ChartScanDetail>(`${base(companyId)}/${scanId}/assign`, payload)
}

export function getChartScanImageUrl(companyId: string | number, scanId: number) {
  return `${base(companyId)}/${scanId}/image`
}

// Fetch the scan image through the authenticated axios instance so the bearer token
// is attached, then return an object URL the browser <img> element can use directly.
// Caller is responsible for revoking via URL.revokeObjectURL when done.
export async function fetchChartScanImageObjectUrl(
  companyId: string | number,
  scanId: number,
): Promise<string> {
  const res = await axiosInstance.get<Blob>(`${base(companyId)}/${scanId}/image`, {
    responseType: 'blob',
  })
  return URL.createObjectURL(res.data)
}

// Fetch the canonical 800x800 PNG of a single disc (WPF's "RotateImage + Cut" output).
// This is what the canvas should render so activity arcs align 1:1 with the visible disc,
// matching the WPF view.
export async function fetchDiscCanonicalImageObjectUrl(
  companyId: string | number,
  discId: number,
): Promise<string> {
  const res = await axiosInstance.get<Blob>(`${base(companyId)}/disc/${discId}/image`, {
    responseType: 'blob',
  })
  return URL.createObjectURL(res.data)
}

export function updateChartDisc(
  companyId: string | number,
  discId: number,
  payload: UpdateDiscPayload,
) {
  return axiosInstance.put<UpdateDiscResponse>(`${base(companyId)}/disc/${discId}`, payload)
}

export function deleteChartDisc(companyId: string | number, discId: number) {
  return axiosInstance.delete(`${base(companyId)}/disc/${discId}`)
}

export function redetectChartDisc(companyId: string | number, discId: number) {
  return axiosInstance.post<ChartScanActivity[]>(`${base(companyId)}/disc/${discId}/redetect`)
}

// Diagnostic dump — raw per-minute MinutesToActivitySize array + derived types +
// merged activity list. Used for diffing against WPF's internal scan output.
export function getChartDiscDiagnostic(companyId: string | number, discId: number) {
  return axiosInstance.get<ChartScanDiagnostic>(`${base(companyId)}/disc/${discId}/diagnostic`)
}

export function getDriverDayNote(
  companyId: string | number,
  driverId: number,
  date: string,
) {
  return axiosInstance.get<{ note: string }>(
    `${base(companyId)}/driver/${driverId}/day-note`,
    { params: { date } },
  )
}

// Odometer the vehicle ended on the last time it was used before `at` (local date-time).
export function getPreviousOdometerEnd(
  companyId: string | number,
  vehicleId: number,
  at: string,
) {
  return axiosInstance.get<{ odometerEnd: number | null }>(
    `${base(companyId)}/vehicle/${vehicleId}/previous-odometer`,
    { params: { at } },
  )
}
